<template>
  <Layout>
    <template #header-container>
      <PageHeader :title="title" isSticky>
        <template #afterTitle>
          <div class="d-flex align-items-center gap-2">
            <McButton
              variant="secondary"
              class="ms-3"
              :text="!showIntegrationShellButton ? $t('global.back') : null"
              icon="icon-outline_arrow-left"
              @click="handleBack"
            />

            <McButton
              v-if="showResponse?.track_link"
              variant="secondary"
              :text="$t('delivery.action.trackDelivery')"
              icon="icon-outline_car"
              @click="handleTrackDO"
            />

            <OtherOptions
              v-if="!isAdding"
              ref="otherOptionsRef"
              :status="deliveryStatus"
              :isLoading="isLoading"
            />

            <McButton
              v-if="!isAdding"
              variant="secondary"
              text=""
              icon="icon-outline_copy"
              :disabled="isLoading"
              @click="handleDuplicate"
            />

            <ScheduleFleetTask v-if="showScheduleFleetTaskButton" :showResponse="showResponse" />

            <McButton
              v-if="showIntegrationShellButton"
              variant="primary"
              :text="$t('delivery.action.updateToShell')"
              @click="handleIntegrationShell"
            />

            <McTransitionStatus
              v-if="showTransitionStatusButton"
              :isCreating="isAdding"
              :entityData="showResponse"
              :disabled="hasChanged"
              :statusTransitions="statusTransitions"
              :getStatusLabelFn="getDeliveryStatusLabel"
              @transition="handleTransitionStatus"
            />
          </div>
        </template>

        <template #footer>
          <b-skeleton-wrapper :loading="isLoading">
            <template #loading>
              <b-skeleton-img no-aspect height="30px" class="mc-wizard-skeleton mt-3" />
            </template>
            <McWizard
              compact
              :items="wizardItems"
              :activeKey="deliveryStatus"
              class="mc-wizard mt-3"
            />
          </b-skeleton-wrapper>
        </template>

        <template #banner>
          <div
            v-if="['Diterima Vendor', 'Ditolak'].includes(deliveryStatus)"
            :class="[
              'vendor-banner mb-2',
              {
                'vendor-banner--success': deliveryStatus === 'Diterima Vendor',
                'vendor-banner--failed': deliveryStatus === 'Ditolak',
              },
            ]"
          >
            {{
              deliveryStatus === 'Diterima Vendor'
                ? $t('delivery.banner.vendorAccepted')
                : $t('delivery.banner.vendorRejected', {
                    reason: showResponse?.rejected_by_vendor_reason,
                  })
            }}
          </div>
          <AlertBanner v-if="accurateDataIsUpdated" :message="alertMessage" />
        </template>
      </PageHeader>
    </template>
    <div class="mc-delivery-form mt-4">
      <Tabs
        class="mc-delivery-tabs"
        :isLoading="isLoading"
        :status="deliveryStatus"
        :visitedStatus="showResponse?.status?.visited_statuses"
        :activeTab="activeTab"
        @update:activeTab="activeTab = $event"
        @showData="showData"
      />

      <hr />

      <template v-if="activeTab == 'INFORMATION'">
        <FormulateForm ref="formRef" v-model="formValues" class="mc-form" :errors="formErrors">
          <div class="mc-header__delivery-info d-flex">
            <h4 class="card-title flex-grow-1">
              {{ $t('delivery.basicInformation') }}
            </h4>
            <McButton
              v-if="!isPickUp && !isTransit"
              variant="secondary"
              class="mx-2"
              :text="$t('global.action.cancel')"
              icon="icon-outline_close"
              :disabled="!hasChanged"
              @click="$refs['mc-modal-cancel'].show()"
            />
            <b-modal
              id="mc-modal-cancel"
              ref="mc-modal-cancel"
              class="mc-modal"
              :title="$t('global.action.cancel')"
              size="md"
              centered
              scrollable
            >
              <div class="card-body p-2">
                <p>
                  {{ $t('global.text.modal.cancelChange') }}
                  <strong>{{ $t('delivery.infoDelivery') }}</strong>
                  ?
                </p>
              </div>
              <template #modal-footer="{ cancel }">
                <div class="d-flex mx-0">
                  <McButton
                    variant="secondary"
                    class="me-2"
                    :text="$t('global.action.no')"
                    @click="cancel()"
                  />
                  <McButton
                    variant="primary"
                    :text="$t('global.action.yes')"
                    @click="handleCancel"
                  />
                </div>
              </template>
            </b-modal>

            <McButton
              v-if="!isPickUp && !isTransit"
              ref="save-button-ref"
              variant="primary"
              type="submit"
              icon="icon-outline_save"
              :text="$t('global.save')"
              :disabled="isSaveButtonDisabled"
              @click="handleSubmit"
            />

            <b-modal
              id="mc-modal-update"
              ref="mc-modal-update"
              class="mc-modal"
              :title="newOriginAddress ? 
                $t('delivery.action.changeOriginAddress') 
                : $t('global.save')"
              size="md"
              centered
              scrollable
            >
              <div class="card-body p-2">
                <p v-if="newOriginAddress">
                  {{ $t('delivery.action.confirmChangeOriginAddress') }}
                  <strong>{{ originAddressFullObject.name }}</strong>
                  {{ $t('global.object.to2') }}
                  <strong>{{ newOriginAddress.name }}</strong>
                </p>
                <p v-else>
                  {{ $t('global.text.modal.saveChange') }}
                  <strong>{{ $t('delivery.infoDelivery') }}</strong>
                  ?
                </p>
              </div>
              <template #modal-footer="{ cancel }">
                <div class="d-flex mx-0">
                  <McButton
                    variant="secondary"
                    class="me-2"
                    :text="$t('global.action.no')"
                    @click="cancel()"
                  />
                  <McButton
                    variant="primary"
                    :text="newOriginAddress ? $t('global.action.updateAndSave') : $t('global.action.yes')"
                    @click="submitUpdate"
                  />
                </div>
              </template>
            </b-modal>
          </div>

          <b-card class="mc-delivery-info__card">
            <b-row class="g-0">
              <h4 class="card-title">
                {{ $t('delivery.title.delivery') }}
              </h4>

              <div class="mc-delivery-info__read-only d-flex">
                <ReadOnly
                  :items="readOnlyDeliveryItems"
                  :detail="showResponse"
                  @additional-act="refetch"
                />

                <div class="w-100 d-flex flex-row-reverse">
                  <!-- Archieved for Refinement -->
                  <div v-if="false" class="d-flex gap-2 h-50">
                    <FormulateInput
                      v-bind="{ ...specialField }"
                      :ref="`${specialField.name}-ref`"
                    />
                    <span class="special-order__text" :class="{ disabled: specialField.disabled }">
                      Order Spesial
                    </span>
                    <i id="special-order-tooltip" class="icon-outline_info"></i>
                    <b-tooltip
                      target="special-order-tooltip"
                      triggers="hover"
                      placement="bottomleft"
                      custom-class="formulate-input-tooltip"
                    >
                      Pengiriman tidak dicampur order lain
                    </b-tooltip>
                  </div>
                </div>
              </div>

              <div class="mc-delivery-info__input-group">
                <div v-for="field in deliveryFields" :key="field.name">
                  <FormulateInput
                    v-if="field.name === 'customer_id'"
                    :ref="`${field.name}-ref`"
                    v-bind="field"
                    :fullObject.sync="shippedCustomerFullObject"
                    class="mc-delivery-info__input"
                    validation-behavior="live"
                    :hasStoreAction="true"
                    :canBeNull="true"
                    identifier="id"
                  />
                  <FormulateInput
                    v-else-if="field.name === 'billed_customer_id'"
                    :ref="`${field.name}-ref`"
                    v-bind="field"
                    :fullObject.sync="billedCustomerFullObject"
                    class="mc-delivery-info__input"
                    validation-behavior="live"
                    :hasStoreAction="true"
                  />
                  <FormulateInput
                    v-else-if="field.name === 'user_group_id'"
                    :ref="`${field.name}-ref`"
                    v-bind="field"
                    :fullObject.sync="userGroupObjSelected"
                    class="mc-delivery-info__input"
                    validation-behavior="live"
                    identifier="id"
                    :validation="
                      field.disabled && deliveryStatus !== 'Dijadwalkan Ulang' ? '' : 'required'
                    "
                    :disabled="field.disabled && deliveryStatus !== 'Dijadwalkan Ulang'"
                  />
                  <FormulateInput
                    v-else-if="field.name === 'marketing_user_id'"
                    :ref="`${field.name}-ref`"
                    v-bind="field"
                    :fullObject.sync="marketingUserObjSelected"
                    class="mc-delivery-info__input"
                    validation-behavior="live"
                  />
                  <FormulateInput
                    v-else
                    v-bind="field"
                    :ref="`${field.name}-ref`"
                    class="mc-delivery-info__input"
                    validation-behavior="live"
                  />
                </div>
              </div>
            </b-row>

            <Switches
              ref="other-delivery-info-switch-ref"
              isSwitched
              class="mc-other-info__switches d-flex gap-2"
              type-bold="true"
              theme="custom"
              :label="$t('global.object.otherInfo')"
              @handle-switch="handleSwitchToggle"
            />

            <b-row v-show="isSwitched">
              <b-col sm="12" md="6">
                <h4 class="card-title mb-2">{{ $t('delivery.note.self') }}</h4>
                <FormulateInput
                  v-for="otherDeliveryField in otherDeliveryFields"
                  :key="otherDeliveryField.name"
                  :ref="`${otherDeliveryField.name}-ref`"
                  :disabled="disableSelfNote(otherDeliveryField)"
                  v-bind="{ ...otherDeliveryField }"
                  :initialObject="
                    otherDeliveryField.getInitialArray
                      ? otherDeliveryField.getInitialArray(showResponse)
                      : undefined
                  "
                  validation-behavior="live"
                  class="px-0"
                />
              </b-col>
              <b-col v-if="isFromVendorRequest" sm="12" md="6">
                <h4 class="card-title mb-3">{{ $t('delivery.note.owner') }}</h4>
                <ReadOnly :items="referenceNotes" orientation="vertical" />
              </b-col>
            </b-row>

            <hr />

            <div class="mc-location__form">
              <div class="mc-origin-location__form">
                <h4 class="card-title">
                  {{ $t('delivery.originLocation') }}
                </h4>
                <ReadOnly
                  :items="readOnlyOriginLocationItems"
                  class="mc-origin-location__read-only"
                />

                <div class="mc-origin-location__input-group">
                  <div
                    v-for="originLocationField in originLocationFields"
                    :key="originLocationField.name"
                    :style="{
                      gridColumn: `span ${originLocationField.gridColSpan || 1} / span ${
                        originLocationField.gridColSpan || 1
                      }`,
                    }"
                  >
                    <FormulateInput
                      :ref="`${originLocationField.name}-ref`"
                      v-bind="{ ...originLocationField }"
                      :fullObjectValue.sync="originAddressFullObject"
                      :initialObject="
                        originLocationField.getInitialObject
                          ? originLocationField.getInitialObject(addressObjSelected)
                          : undefined
                      "
                      :help="originLocationField?.validation?.includes('required')"
                      validation-behavior="live"
                      :class="[
                        'p-0',
                        { 'mb-2': !originLocationField?.validation?.includes('required') },
                      ]"
                    >
                      <template v-if="originLocationField.icon" #suffix>
                        <div class="formulate-input__icon">
                          <i
                            :class="originLocationField.icon"
                            @click="
                              () =>
                                $refs[`${originLocationField.name}-ref`][0].$el
                                  .querySelector('input')
                                  .focus()
                            "
                          ></i>
                        </div>
                      </template>
                    </FormulateInput>
                    <div style="display:flex; gap:10px">
                      <ButtonCopy
                        v-if="originLocationField.name === 'origin_address_id'"
                        icon="icon-outline_copy"
                        :buttonText="$t('delivery.action.copyAddress')"
                        :valueToCopy="addressObjSelected[originLocationField.name]?.name"
                        :toastTitle="originLocationField.label"
                        :disabled="!addressObjSelected[originLocationField.name]?.name"
                      />
                      <McButtonValueHelp
                        v-if="
                          originLocationField.name === 'origin_address_id'
                          && isSamator
                          && ['Dikonfirmasi', 'Dijadwalkan Ulang'].includes(showResponse?.status?.name)
                        "
                        variant="secondary"
                        icon="icon-outline_edit"
                        size="sm"
                        :text="$t('delivery.action.changeAddress')"
                        :label="$t('delivery.action.chooseOriginAddress')"
                        :multiple="false"
                        :trackBy="originLocationField.trackBy || 'id'"
                        :listGetter="originLocationField.listGetter"
                        :tableFields="originLocationField.tableFields"
                        :disabled="isLoading"
                        @selected="($event) => handleAddressValueHelpApply($event)"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div class="mc-delivery-location__form">
                <h4 class="card-title">
                  {{ $t('delivery.destinationLocation') }}
                </h4>
                <ReadOnly
                  :items="readOnlyDeliveryLocationItems"
                  class="mc-delivery-location__read-only"
                />

                <div class="mc-delivery-location__input-group">
                  <div
                    v-for="destinationLocationField in destinationLocationFields"
                    :key="destinationLocationField.name"
                    sm="12"
                    md="6"
                    :style="{
                      gridColumn: `span ${destinationLocationField.gridColSpan || 1} / span ${
                        destinationLocationField.gridColSpan || 1
                      }`,
                    }"
                  >
                    <FormulateInput
                      :ref="`${destinationLocationField.name}-ref`"
                      v-bind="{ ...destinationLocationField }"
                      :fullObjectValue.sync="destinationAddressFullObject"
                      :initialObject="
                        destinationLocationField.getInitialObject
                          ? destinationLocationField.getInitialObject(addressObjSelected)
                          : undefined
                      "
                      :help="destinationLocationField?.validation?.includes('required')"
                      validation-behavior="live"
                      :class="[
                        'p-0',
                        { 'mb-2': !destinationLocationField?.validation?.includes('required') },
                      ]"
                    >
                      <template v-if="destinationLocationField.icon" #suffix>
                        <div class="formulate-input__icon">
                          <i
                            :class="destinationLocationField.icon"
                            @click="
                              () =>
                                $refs[`${destinationLocationField.name}-ref`][0].$el
                                  .querySelector('input')
                                  .focus()
                            "
                          ></i>
                        </div>
                      </template>
                    </FormulateInput>
                    <ButtonCopy
                      v-if="destinationLocationField.name === 'destination_address_id'"
                      icon="icon-outline_copy"
                      :buttonText="$t('delivery.action.copyAddress')"
                      :valueToCopy="addressObjSelected[destinationLocationField.name]?.name"
                      :toastTitle="destinationLocationField.label"
                      :disabled="!addressObjSelected[destinationLocationField.name]?.name"
                    />
                  </div>
                </div>
              </div>
            </div>

            <template v-if="isCustomFieldShown">
              <hr />

              <h4 class="card-title mb-2">{{ $t('delivery.additionalFields.title') }}</h4>
              <b-row v-if="customFields.length > 0" class="g-3">
                <b-col v-for="customField in customFields" :key="customField.udfId" sm="12" md="4">
                  <FormulateInput v-bind="customField" validation-behavior="live" class="p-0" />
                </b-col>
              </b-row>
              <div v-else class="d-flex align-items-center gap-4">
                <p class="mb-0">
                  {{ $t('delivery.additionalFields.description') }}
                </p>
                <McButton
                  :text="$t('delivery.additionalFields.addButton')"
                  size="sm"
                  icon="icon-fill_plus"
                  @click="
                    $router.push({ query: { isModalShown: true }, path: '/setting/custom-field' })
                  "
                />
              </div>
            </template>

            <template v-if="isVendorFieldShown">
              <hr />

              <h4 class="mc-vendor-name__title card-title">{{ $t('delivery.fields.vendor') }}</h4>
              <b-row class="g-3">
                <b-col sm="12" md="3">
                  <FormulateInput
                    v-if="hasAccessAll(['vms:create', 'vms:read', 'vms:update', 'vms:delete'])"
                    :ref="`${vendorField.name}-ref`"
                    v-bind="{ ...vendorField }"
                    :fullObject.sync="vendorFullObject"
                    class="p-0"
                    validation-behavior="live"
                  />
                </b-col>
                <!-- Please do not delete for future enhancement -->
                <!-- <b-col class="vendor-fleet-status" sm="12" md="3">
                  <div class="__label">Status Penugasan Vendor</div>
                  <div class="__selected">{{ vendorFullObject.status?.name || '-' }}</div>
                </b-col> -->
              </b-row>
            </template>
          </b-card>
        </FormulateForm>

        <LoadsTable
          v-if="activeTab == 'INFORMATION' && isDataComplete"
          ref="loads-table"
          :apiClient="loadApiClient"
          :status="deliveryStatus"
          :invalidFields="invalidLoadFields"
          :totalWeights="totalWeights"
          :totalVolumes="totalVolumes"
          :loadMinTemp="loadMinTemp"
          :loadMaxTemp="loadMaxTemp"
          :isLoadAdjusted="isLoadAdjusted"
          :canAddProduct="canAddProduct"
          @update:totalWeights="handleChangeTotalWeights"
          @update:totalVolumes="handleChangeTotalVolumes"
          @refetch-show-delivery="showData"
        />
      </template>

      <template v-else>
        <ProofTabV2
          :key="activeTab"
          :activeTab="activeTab"
          @update:isLoading="isLoading = $event"
        />
      </template>
    </div>

    <b-modal
      id="mc-modal-back"
      ref="mc-modal-back"
      class="mc-modal"
      :title="$t('global.confirmation.title')"
      size="md"
      centered
      scrollable
    >
      <div class="card-body p-2">
        <p>{{ $t('global.confirmation.question') }}</p>
        <p>{{ $t('global.confirmation.sure') }}</p>
      </div>
      <template #modal-footer="{ cancel }">
        <div class="d-flex mx-0">
          <McButton
            variant="secondary"
            class="me-2"
            :text="$t('global.confirmation.exitPage')"
            @click="routingBack"
          />
          <McButton
            variant="primary"
            :text="$t('global.confirmation.stayPage')"
            @click="cancel()"
          />
        </div>
      </template>
    </b-modal>
    <ModalShareTracking :detailData="showResponse" />
    <ModalIntegrationShell :detailData="showResponse" />
    <ModalValueHelp
      ref="mc-modal-user-group"
      :context="userGroupContext"
      :attrs="userGroupAttrs"
      @update:fullObject="handleUserGroupSelect"
    />
  </Layout>
</template>

<script>
import appConfig from '@/app.config'
import { mapState, mapGetters } from 'vuex'
import { MixPanel } from '@/mixin/mixpanel'
import ButtonCopy from './components/ButtonCopy.vue'

import Switches from './components/Switches.vue'
import unmaskFormValues from '@/components/formulate/TextInput/lib/unmaskFormValues'
import maskFormValues from '@/components/formulate/TextInput/lib/maskFormValues'
import { isEqual, isEmpty } from 'lodash'

import Layout from '@/router/layouts/main'
import PageHeader from '@/components/page-header-v2.vue'
import McButton from '@/components/McButton'
import McButtonValueHelp from '@/components/McButtonValueHelp'
import McWizard from '@/components/McWizard'
import McAlert from '@/components/McAlert'
import McTransitionStatus from '@/components/McTransitionStatus'
import ModalValueHelp from '@/components/formulate/ValueHelpInputV2/components/ModalValueHelp'
import McCheckbox from '@/components/McCheckbox'
import McInfoTooltip from '@/components/McInfoTooltip'
import ReadOnly from './components/ReadOnly.vue'
import LoadsTable from './components/LoadsTable/index.vue'
import OtherOptions from './components/OtherOptions.vue'
import ModalShareTracking from './components/Modal/ShareTracking'
import ModalIntegrationShell from './components/Modal/IntegrationShell'
import Tabs from './components/Tabs'
import ProofTabV2 from './components/ProofTab-v2'
import ScheduleFleetTask from './components/ScheduleFleetTask'
import AlertBanner from './components/AlertBanner'
import FleetTaskMultiFoDropdown from './components/FleetTaskMultiFoDropdown.vue'

import * as DeliveryOrder from '@/services/rest-api/lib/delivery/delivery-order'
import * as DeliveryNote from '@/services/rest-api/lib/customer/delivery-note'
import * as Address from '@/services/rest-api/lib/delivery/address'
import * as Load from '@/services/rest-api/lib/delivery/load'
import * as Transition from '@/services/rest-api/lib/delivery/transition'
import * as Vendor from '@/services/rest-api/lib/vendor/vendor'
import * as CustomField from '@/services/rest-api/lib/setting/custom-field'
import * as DeliveryActivity from '@/services/rest-api/lib/delivery/activity'

import { GET_USER_GROUP } from '@/services/api/setting/user/userGroup'

import apiErrorMappings from './lib/constants/api-error-mappings'
import statusTransitions from './lib/constants/status-transitions'
import { wizardStatusItems } from './lib/constants/wizard-status-items'

import moment from 'moment'
import cloneDeep from 'lodash/cloneDeep'
import { Helper } from '@/mixin/helper'
import { isTmsMarketingUser } from '@/helpers/tms-access'
import { hasLineOrderFeatureAccess } from '@/helpers/tms-line-order'

moment.locale('id')

import {
  getReadOnlyDeliveryItems,
  getReadOnlyOriginLocationItems,
  getReadOnlyDeliveryLocationItems,
} from './lib/readOnlyItems'

import specialField from './lib/fields/specialField'
import getDeliveryDetailFields from './lib/fields/deliveryDetailFields'
import getDeliveryOtherFields from './lib/fields/deliveryOtherFields'
import getOriginAddressFields from './lib/fields/originAddressFields'
import getDestinationAddressFields from './lib/fields/destinationAddressFields'
import getVendorFields from './lib/fields/vendorFields.js'
import { formatDateWithTz } from '@/helpers/tzDate'
import { statusIdToLabelMap, statusNameToIdMap } from './lib/constants/status'

export default {
  page: {
    title: 'Form Delivery',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McButton,
    McButtonValueHelp,
    McWizard,
    ReadOnly,
    Switches,
    LoadsTable,
    OtherOptions,
    McTransitionStatus,
    Tabs,
    ProofTabV2,
    ButtonCopy,
    ScheduleFleetTask,
    AlertBanner,
    ModalShareTracking,
    ModalIntegrationShell,
    ModalValueHelp,
    FleetTaskMultiFoDropdown,
  },
  mixins: [MixPanel, Helper],
  data() {
    return {
      isSubmitting: false,
      isSwitched: false,
      isLoading: false,

      deliveryNumber: '',
      fleetTaskNumber: '',
      shipmentType: '',
      refOrder: null,
      activeTab: 'INFORMATION',
      alertMessage: '',

      shippedCustomerFullObject: {},
      billedCustomerFullObject: {},
      marketingUserObjSelected: {},
      userGroupObjSelected: {},
      addressObjSelected: {},
      boxTypeFullObject: {},
      vendorFullObject: {},

      deliveryOrderData: {},
      formValues: {},
      invalidFormValues: {},
      formErrors: {},
      errorResponse: {},
      invalidLoadFields: [],

      showResponse: null,
      totalWeights: null,
      totalVolumes: null,

      readOnlyDeliveryItems: getReadOnlyDeliveryItems(this.$t.bind(this)),
      readOnlyOriginLocationItems: getReadOnlyOriginLocationItems(this.$t.bind(this)),
      readOnlyDeliveryLocationItems: getReadOnlyDeliveryLocationItems(this.$t.bind(this)),

      defaultCustomFields: [],
      originAddressFullObject: {},
      destinationAddressFullObject: {},
      accurateDataIsUpdated: false,
      transits: [],
      isLoadAdjusted: false,
      cachedCompleteDestinationTransit: false,
      hasCalculatedOnce: false,
      userGroupContext: {
        label: 'User Group',
        model: null,
        blurHandler: () => {},
      },
      skipValidationUserGroup: false,
      loadMinTemp: null,
      loadMaxTemp: null,
      isBoxTypeChanges: false,
      canAddProduct: true,
      isDataComplete: false,
      isFromTransition: false,
      newOriginAddress: null,
    }
  },
  computed: {
    ...mapState('delivery', ['deliveryStatus']),
    ...mapGetters({
      hasAccessAll: 'identity/hasAccessAll',
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    title() {
      return this.$route.params.id
        ? `${this.$t('delivery.title.detail')} #${this.deliveryNumber}`
        : `${this.$t('delivery.title.create')}`
    },
    wizardStatusItems() {
      return wizardStatusItems(this)
    },
    wizardItems() {
      if (this.deliveryStatus == 'Dijadwalkan Ulang') {
        return [
          this.wizardStatusItems.RESCHEDULED,
          this.wizardStatusItems.SCHEDULED,
          this.wizardStatusItems.STARTED,
          this.wizardStatusItems.PICKED,
          this.wizardStatusItems.DELIVERED,
        ]
      }

      if (this.deliveryStatus == 'Dibatalkan') {
        return this.showResponse?.status.visited_statuses.map((status) => {
          return this.wizardStatusItems[status]
        })
      }

      if (this.deliveryStatus == 'Ditolak') {
        return [
          this.wizardStatusItems.DRAFT,
          this.wizardStatusItems.CONFIRMED,
          this.wizardStatusItems.WAITING_FOR_VENDOR,
          this.wizardStatusItems.REJECTED_BY_VENDOR,
        ]
      }

      if (
        this.isTransit ||
        (!this.isTransit && this.showResponse?.status.visited_statuses.includes('INTRANSIT'))
      ) {
        const items = [
          this.wizardStatusItems.DRAFT,
          this.wizardStatusItems.CONFIRMED,
          this.wizardStatusItems.SCHEDULED,
          this.wizardStatusItems.STARTED,
          this.wizardStatusItems.PICKED,
          this.wizardStatusItems.TRANSITED,
          this.wizardStatusItems.DELIVERED,
        ]

        if (this.isSubscribeVMS && this.isRequestedToVendor) {
          items.splice(
            2,
            0,
            this.wizardStatusItems.WAITING_FOR_VENDOR,
            this.wizardStatusItems.ACCEPTED_BY_VENDOR
          )
        }

        return items
      }

      if (
        this.shipmentType === 'DIRECT' &&
        this.showResponse?.status.name === 'Sedang Transit' &&
        this.isSubscribeVMS &&
        this.isRequestedToVendor
      ) {
        const items = [
          this.wizardStatusItems.DRAFT,
          this.wizardStatusItems.CONFIRMED,
          this.wizardStatusItems.WAITING_FOR_VENDOR,
          this.wizardStatusItems.ACCEPTED_BY_VENDOR,
          this.wizardStatusItems.SCHEDULED,
          this.wizardStatusItems.STARTED,
          this.wizardStatusItems.PICKED,
          this.wizardStatusItems.TRANSITED,
          this.wizardStatusItems.DELIVERED,
        ]

        return items
      }

      const items = [
        this.wizardStatusItems.DRAFT,
        this.wizardStatusItems.CONFIRMED,
        this.wizardStatusItems.SCHEDULED,
        this.wizardStatusItems.STARTED,
        this.wizardStatusItems.PICKED,
        this.wizardStatusItems.DELIVERED,
      ]

      // If user subscribes to vms and there is a selected vendor,
      // insert `Menunggu Vendor` and `Diterima Vendor` statuses
      // after `Dikonfirmasi` status
      if (this.isSubscribeVMS && this.isRequestedToVendor) {
        items.splice(
          2,
          0,
          this.wizardStatusItems.WAITING_FOR_VENDOR,
          this.wizardStatusItems.ACCEPTED_BY_VENDOR
        )
      }

      return items
    },
    specialField() {
      if (!this.deliveryStatus) return specialField.default
      return specialField[this.deliveryStatus]
    },
    deliveryFields() {
      const deliveryDetailFields = getDeliveryDetailFields(this.$t.bind(this), this.$i18n.locale)
      const fields = !this.deliveryStatus
        ? deliveryDetailFields.default
        : deliveryDetailFields[this.deliveryStatus]

      return fields.filter((f) => f.isShow === undefined || f.isShow === true)
    },
    otherDeliveryFields() {
      const deliveryOtherFields = getDeliveryOtherFields(this.$t.bind(this), this.$i18n.locale)
      if (!this.deliveryStatus) return deliveryOtherFields.default

      const fields = deliveryOtherFields[this.deliveryStatus]
      const placeholderText =
        this.deliveryOrderData.customer_id === null
          ? this.$t('delivery.noteField.undefined')
          : this.$t('delivery.noteField.selectCustomerSpecial')

      fields.forEach((field) => {
        if (field.name === 'customer_notes') {
          field.placeholder = placeholderText
        }
      })

      return fields
    },
    originLocationFields() {
      const originAddressFields = getOriginAddressFields(this.$t.bind(this), this.$i18n.locale)
      if (!this.deliveryStatus) return originAddressFields.default
      return originAddressFields[this.deliveryStatus]
    },
    destinationLocationFields() {
      const destinationAddressFields = getDestinationAddressFields(
        this.$t.bind(this),
        this.$i18n.locale
      )
      if (!this.deliveryStatus) return destinationAddressFields.default
      return destinationAddressFields[this.deliveryStatus]
    },
    vendorField() {
      const vendorFields = getVendorFields(this.$t.bind(this), this.$i18n.locale)
      const field = !this.deliveryStatus ? vendorFields.default : vendorFields[this.deliveryStatus]

      // TODO: Logic to assign the value need to be optimized
      if (field.headerInfoComponent) {
        const newItems = field.headerInfoProps.items.map((item) => {
          const newItem = Object.assign({}, item)
          if (item.key == 'box_type') {
            newItem.value = this.boxTypeFullObject?.name || '-'
          } else if (item.key == 'origin') {
            newItem.value = this.addressObjSelected?.origin_address_id?.city || '-'
          } else if (item.key == 'destination') {
            newItem.value = this.addressObjSelected?.destination_address_id?.city || '-'
          } else if (item.key == 'total_weight') {
            newItem.value = this.selectedBoxCapacity || '-'
          }

          return newItem
        })

        field.headerInfoProps.items = newItems
      }

      const filters = ['status.name eq ACTIVE', 'paired_company_id gt 0']
      let originAddressId = this.addressObjSelected['origin_address_id']?.id
      let destinationAddressId = this.addressObjSelected['destination_address_id']?.id

      if (!originAddressId || !destinationAddressId) {
        originAddressId = undefined
        destinationAddressId = undefined
      }

      field.listGetter = Vendor.getRatesWithFilters(filters, originAddressId, destinationAddressId)

      return field
    },
    customerNameFields() {
      return [
        {
          name: 'customer_name',
          showKey: 'shipped_customer',
          trackBy: 'name',
        },
        {
          name: 'billed_customer_name',
          showKey: 'billed_customer',
          trackBy: 'name',
        },
      ]
    },
    fields() {
      return [
        ...this.deliveryFields,
        ...this.otherDeliveryFields,
        ...this.originLocationFields,
        ...this.destinationLocationFields,
        ...this.customerNameFields,
        this.specialField,
        this.vendorField,
      ]
    },
    fieldKeys() {
      return this.fields.reduce((obj, val) => {
        obj[val.name] = ''
        return obj
      }, {})
    },
    isAdding() {
      return this.$route.params.id === undefined
    },
    isPickUp() {
      return ['Diambil', 'Terkirim'].includes(this.deliveryStatus)
    },
    isVendorFieldShown() {
      return (
        this.isSubscribeVMS &&
        !this.isFromVendorRequest &&
        (this.isAdding ||
          ['Draf', 'Ditolak', 'Dikonfirmasi'].includes(this.deliveryStatus) ||
          this.isRequestedToVendor)
      )
    },
    isRequestedToVendor() {
      return this.vendorFullObject && Object.keys(this.vendorFullObject).length > 0
    },
    isSubscribeVMS() {
      return this.hasAccessAll(['vms:create', 'vms:read', 'vms:update', 'vms:delete'])
    },
    isSipmentTypeTransit() {
      return this.shipmentType === 'TRANSIT'
    },
    statusTransitions() {
      return statusTransitions(
        this.isSubscribeVMS,
        this.isRequestedToVendor,
        this.isFromVendorRequest,
        ['Diambil'].includes(this.deliveryStatus),
        this.isSipmentTypeTransit,
        this.hasTmsMarketingFlag
      )
    },
    hasChanged() {
      if (this.isLoading) return false

      let currentFormValues = this.formValues
      let oldFormValues = {}

      if (!this.isAdding) oldFormValues = this.deliveryOrderData

      const res = this.filterUnchangedData(currentFormValues, oldFormValues)

      return res[1]
    },
    loadApiClient() {
      if (!this.$route.params.id) return {}
      return {
        get: Load.get(this.$route.params.id),
        show: Load.show(this.$route.params.id),
        update: Load.update(this.$route.params.id),
        store: Load.store(this.$route.params.id),
        delete: Load.deleteSingle(this.$route.params.id),
      }
    },
    invalidFieldKeys() {
      return Object.keys(this.invalidFormValues)
    },
    referenceNotes() {
      return [
        {
          label: 'Catatan Khusus Pelanggan',
          value: this.showResponse?.reference_info_customer_notes.join(';'),
        },
        {
          label: 'Catatan Lainnya',
          value: this.showResponse?.reference_info_notes.join(';'),
        },
      ]
    },
    isCustomFieldShown() {
      return this.customFields.length > 0
    },
    customFields() {
      let transformed = []

      /**
       * When creating a new DO, use the default custom field setting
       * When updating a DO, use the fields from the response
       */
      transformed = this.transformCustomFieldResponse(
        this.isAdding ? this.defaultCustomFields : this.showResponse?.user_definition_fields
      )

      return transformed
    },
    isTransit() {
      return (
        this.shipmentType === 'TRANSIT' &&
        [
          'Sedang Transit',
          'Dijadwalkan',
          'Dijalankan',
          'Diambil',
          'Menunggu Vendor',
          'Diterima Vendor',
          'Ditolak Vendor',
        ].includes(this.deliveryStatus)
      )
    },
    showIntegrationShellButton() {
      return (
        !this.isLoading &&
        this.hasAccessAll(['shell-integration:read', 'shell-integration:create']) &&
        ['Dijalankan', 'Diambil', 'Sedang Transit', 'Terkirim'].includes(this.deliveryStatus)
      )
    },
    hasTmsUserGroupAccess() {
      return (
        this.hasAccessAll([
          'tms-user-group:create',
          'tms-user-group:delete',
          'tms-user-group:read',
          'tms-user-group:update',
        ]) && this.isSuperUser()
      )
    },
    userGroupAttrs() {
      return {
        name: 'user_group',
        modalTitle: 'Pilih Grup Pengguna',
        trackBy: 'id',
        tableFields: [
          {
            label: 'Grup Pengguna',
            key: 'name',
            isSortable: false,
          },
          {
            label: 'Grup Kendaraan',
            key: 'vgroups',
            isSortable: false,
            formatter: (value) => {
              return value?.length != null
                ? value.length === 0
                  ? 'Semua Grup Kendaraan'
                  : value.length
                : '-'
            },
          },
        ],
        listGetter: async function (opts) {
          let result = {}
          const filter = {
            pagination: {
              page: opts?.page,
              show: opts?.limit,
            },
            search: opts?.search,
          }

          const res = await this.$apollo.query({
            query: GET_USER_GROUP,
            fetchPolicy: 'no-cache',
            variables: {
              filter: {
                ...filter,
              },
            },
          })

          const ugroups = res.data.ugroups.ugroups

          result = {
            metadata: {
              total_count: ugroups.pagination.total,
              page: ugroups.pagination.page,
            },
            data: {
              paginated_result: ugroups.data,
            },
          }

          return result
        },
        isMandatory: true,
      }
    },
    hasUserGroup() {
      return this.showResponse?.user_group !== null
    },
    showTransitionStatusButton() {
      const hasAccess = !this.hasTmsUserGroupAccess || this.hasUserGroup
      const shouldHideForTmsMarketing = this.isTmsMarketing

      if (this.hasTmsMarketingFlag && this.deliveryStatus === 'Draf') {
        return true
      }

      return hasAccess && !shouldHideForTmsMarketing
    },
    showScheduleFleetTaskButton() {
      const hasScheduleAccess =
        ((['Dikonfirmasi', 'Dijadwalkan Ulang'].includes(this.deliveryStatus) &&
          !this.isRequestedToVendor) ||
          this.isTransit) &&
        !this.getCompleteDestinationTransit()

      if (this.isTmsMarketing) {
        return this.hasTmsMarketingFlag && hasScheduleAccess
      }

      return hasScheduleAccess
    },
    hasTmsMarketingFlag() {
      return this.isTmsMarketing
    },
    isSamator(){
      const data = this.getFeatureFlagValue('customFeature.samator')
      return Array.isArray(data?.company_id) && data.company_id.includes(this.getCompanyId())
    },
    canEditSave() {
      if (this.isAdding) return true

      const isTmsMarketing = this.hasTmsMarketingFlag

      const allowedDeliveryStatus = isTmsMarketing
        ? ['Draf', 'Dikonfirmasi', 'Dijadwalkan Ulang']
        : ['Draf']

      const allowedResponseStatus = isTmsMarketing
        ? ['DRAFT', 'CONFIRMED', 'RESCHEDULED']
        : ['DRAFT']

      return (
        allowedDeliveryStatus.includes(this.deliveryStatus) ||
        allowedResponseStatus.includes(this.showResponse?.status?.id)
      )
    },
    isSaveButtonDisabled() {
      if (!this.isTmsMarketing) return !this.hasChanged

      return !this.hasChanged || !this.canEditSave
    },
    isFleetandProductBoxTypeSame() {
      return true
    },
    isTmsMarketing() {
      return isTmsMarketingUser()
    },
    tmsLineOrder() {
      return hasLineOrderFeatureAccess() ? this.showResponse.line_order : '-'
    },
  },
  watch: {
    '$i18n.locale': function () {
      this.refreshReadOnlyItemLabels()
    },
    async formValues() {
      if (this.invalidFieldKeys.length === 0) return

      this.invalidFieldKeys.forEach((fieldErrorKey) => {
        if (this.invalidFormValues[fieldErrorKey] != this.formValues[fieldErrorKey]) {
          // delete the field from the state to show errors
          this.$delete(this.formErrors, fieldErrorKey)
          this.$delete(this.invalidFormValues, fieldErrorKey)
        }
      })

      // empty the errorResponse if all error key is deleted
      if (this.invalidFieldKeys.length === 0) {
        this.errorResponse = {}
        this.invalidFormValues = {}
      }
    },
    // TODO: rewrite to apply the side effect of the changes this key
    'formValues.customer_id': async function (newValue, oldValue) {
      // value doesn't change
      if (newValue == oldValue) return

      // If user unselects customer_id
      if (newValue === undefined) {
        this.marketingUserObjSelected = {}
        this.billedCustomerFullObject = {}
        this.$delete(this.formValues, 'marketing_user_id')
        this.$delete(this.formValues, 'billed_customer_id')
        this.$delete(this.formValues, 'customer_notes')

        // Currently, there's now way that choosen customer id will be null
        // Except for order that created by third party
        // So for now, this name value is used for marking if the data is same or not
        // (because for now the comparison is using id, so there's no way to tell the difference between customer with null id or when field is empty,
        // so that's why we are using customer name)

        this.$delete(this.formValues, 'customer_name')
        this.$delete(this.formValues, 'billed_customer_name')

        // change listGetter without filters
        await this.assignAddressListGetter('origin_address', undefined)
        await this.assignAddressListGetter('destination_address', undefined)

        // reset customer_notes select resourceful properties
        this.$refs['customer_notes-ref'][0].$children[1].ResetProperties()
        return
      }

      // if user selects customer_id
      // Change marketing_user when customer_id changes
      // TODO: Need to find better way to get marketing user object
      const marketingUser =
        this.shippedCustomerFullObject.marketing_user ?? this.marketingUserObjSelected

      // if marketing user is not exist on customer
      this.$delete(this.formValues, 'marketing_user_id')

      if (marketingUser.id !== null) {
        // if marketing user exist on customer
        this.$set(this.formValues, 'marketing_user_id', marketingUser.id)
      }

      this.marketingUserObjSelected = marketingUser

      // Set billed customer to be equal to shipped customer
      this.$set(this.formValues, 'billed_customer_id', newValue)
      this.billedCustomerFullObject = this.shippedCustomerFullObject

      // CHANGE CUSTOMER NOTES FIELD
      // change customer notes when customer_id change
      const customerNotesFieldIdx = this.otherDeliveryFields.findIndex(
        (field) => field.name === 'customer_notes'
      )
      if (customerNotesFieldIdx === -1) return

      // change options getter of customer notes
      this.$set(
        this.otherDeliveryFields[customerNotesFieldIdx],
        'optionsGetter',
        DeliveryNote.get(this.formValues.customer_id)
      )

      // reset customer_notes select resourceful properties
      this.$refs['customer_notes-ref'][0].$children[1].ResetProperties()

      // reset selected customer notes if changed from dropdown
      if (this.showResponse?.shipped_customer?.id !== newValue) {
        this.$delete(this.formValues, 'customer_notes')
      } else {
        // getting back the customer_notes if the customer change back to customer in showResponse
        this.$set(this.formValues, 'customer_notes', this.showResponse.customer_notes)
      }

      // CHANGE ADDRESS FIELD
      if (this.showResponse?.shipped_customer?.id !== null) {
        // change origin address field
        await this.assignAddressListGetter('origin_address', newValue)

        // change destination address field
        await this.assignAddressListGetter('destination_address', newValue)
      }
    },
    'formValues.origin_address_id': async function (newValue, oldValue) {
      await this.assignAddressValues({
        key: 'origin_address',
        otherKey: 'destination_address',
        stateName: 'readOnlyOriginLocationItems',
        initial: getReadOnlyOriginLocationItems(this.$t.bind(this)),
        newValue,
        oldValue,
      })
    },
    'formValues.destination_address_id': async function (newValue, oldValue) {
      await this.assignAddressValues({
        key: 'destination_address',
        otherKey: 'origin_address',
        stateName: 'readOnlyDeliveryLocationItems',
        initial: getReadOnlyDeliveryLocationItems(this.$t.bind(this)),
        newValue,
        oldValue,
      })
    },
    'formValues.box_type_id': async function (newValue, oldValue) {
      // if unselect box type
      if (newValue === undefined || newValue === null) return

      // value doesn't change
      if (newValue === oldValue) return
    },
  },

  async mounted() {
    if (!this.$route.params.id) {
      await this.$store.dispatch('delivery/setDeliveryStatus', '')
      await this.$store.dispatch('delivery/setFleetTaskId', '')
      await this.$store.dispatch('delivery/setActivities', [])
      this.getDefaultCustomFields()

      await this.assignAddressListGetter('origin_address', undefined)
      await this.assignAddressListGetter('destination_address', undefined)
      return
    }

    if (this.$route.query?.duplicated == 'true') {
      this.$mcToast.success(this.$t('delivery.alert.duplicateSuccess'))
    }

    this.showData()
  },
  methods: {
    getDeliveryStatusLabel(status) {
      if (!status) return '-'
      if (status.label) return this.$t(status.label)

      const statusId = status.id || statusNameToIdMap[status.name]
      const statusLabel = statusIdToLabelMap[statusId]

      return statusLabel ? this.$t(statusLabel) : status.name
    },
    mergeReadOnlyItemLabels(items = [], translatedItems = []) {
      const translatedItemsByKey = translatedItems.reduce((acc, item) => {
        acc[item.key] = item
        return acc
      }, {})

      return items.map((item) => ({
        ...translatedItemsByKey[item.key],
        ...item,
        label: translatedItemsByKey[item.key]?.label || item.label,
        value:
          item.defaultValue !== undefined && item.value === item.defaultValue
            ? translatedItemsByKey[item.key]?.defaultValue ??
              translatedItemsByKey[item.key]?.value ??
              item.value
            : item.value,
        defaultValue: translatedItemsByKey[item.key]?.defaultValue ?? item.defaultValue,
      }))
    },
    refreshReadOnlyItemLabels() {
      const t = this.$t.bind(this)

      this.readOnlyDeliveryItems = this.mergeReadOnlyItemLabels(
        this.readOnlyDeliveryItems,
        getReadOnlyDeliveryItems(t)
      )
      this.readOnlyOriginLocationItems = this.mergeReadOnlyItemLabels(
        this.readOnlyOriginLocationItems,
        getReadOnlyOriginLocationItems(t)
      )
      this.readOnlyDeliveryLocationItems = this.mergeReadOnlyItemLabels(
        this.readOnlyDeliveryLocationItems,
        getReadOnlyDeliveryLocationItems(t)
      )
    },
    async assignAddressListGetter(key, newCustomerId) {
      // set fields
      let fields
      if (key === 'origin_address') {
        fields = [...this.originLocationFields]
      } else if (key === 'destination_address') {
        fields = [...this.destinationLocationFields]
      }

      // find the index of the key in fields
      const addressFieldIdx = fields.findIndex((field) => field.name === `${key}_id`)
      if (addressFieldIdx === -1) return

      // assign the new listGetter
      let newListGetter
      if (newCustomerId === undefined) {
        newListGetter = Address.get()
      } else if (newCustomerId === null) {
        newListGetter = Address.get('customer_id is null')
      } else newListGetter = Address.get(`with_customer_id eq '${newCustomerId}'`)

      // change list getter of address
      this.$set(fields[addressFieldIdx], 'listGetter', newListGetter)

      // reset selected address if changed from value help
      if (!this.isAdding && this.showResponse?.shipped_customer?.id == newCustomerId) {
        this.$set(this.formValues, `${key}_id`, this.showResponse[key].id)
        await this.$nextTick()
        return
      }

      this.$delete(this.formValues, `${key}_id`)

      if (key === 'origin_address') {
        this.originAddressFullObject = {}
      } else if (key === 'destination_address') {
        this.destinationAddressFullObject = {}
      }
    },
    async assignAddressValues({ key, otherKey, stateName, newValue, oldValue, initial }) {
      // if the value doesn't change
      if (newValue === oldValue) return

      // if user select origin address
      if (newValue === this.formValues[`${otherKey}_id`] && newValue != null) {
        this.$mcToast.error(this.$t('delivery.alert.sameAddress'))
        this.$delete(this.formValues, `${key}_id`)
        return
      }

      let data
      const fullObject = this.$refs[`${key}_id-ref`][0].$children[1].fullObject
      if (!this.isAdding) {
        // show data if not in create form
        data = !isEmpty(fullObject) ? fullObject : this.showResponse[key]
      } else {
        // if in create form
        data = fullObject
      }

      if (!data) return
      this[stateName].forEach((item, index) => {
        let value

        switch (item.key) {
          case 'operationalTime':
            if (!data.start_operational_on || !data.end_operational_on) value = '-'
            else value = `${data.start_operational_on} - ${data.end_operational_on}`
            break

          case 'breakTime':
            if (!data.start_break_on || !data.end_break_on) value = '-'
            else value = `${data.start_break_on} - ${data.end_break_on}`
            break

          default:
            value = data[`${item.key}`] || '-'
            break
        }

        this.$set(this[stateName], index, {
          ...item,
          value,
          bold: true,
        })
        this.$set(this.addressObjSelected, `${key}_id`, data)
      })
    },
    handleChangeTotalWeights(newValue) {
      this.totalWeights = newValue
    },
    handleChangeTotalVolumes(newValue) {
      this.totalVolumes = newValue
    },
    parseData(data) {
      const parsedData = {}
      this.fields.forEach((field) => {
        if (data[field.name] === undefined && data[field.showKey] === undefined) return
        parsedData[field.name] = field.showKey ? data[field.showKey]?.id : data[field.name]

        switch (field.name) {
          case 'expected_pickup_on':
            if (!data.expected_pickup_on) break
            parsedData[field.name] = moment(parsedData[field.name]).format(
              'YYYY-MM-DDTHH:mm:ss.SSSZ'
            )
            break

          case 'expected_delivered_on':
            if (!data.expected_delivered_on) break
            parsedData[field.name] = moment(parsedData[field.name]).format(
              'YYYY-MM-DDTHH:mm:ss.SSSZ'
            )
            break

          case 'delivery_notes':
          case 'customer_notes':
            if (data[field.name].length === 0) delete parsedData[field.name]
            break

          case 'origin_service_time':
          case 'destination_service_time':
            var d = moment.duration(parsedData[field.name])
            var days = d.days()
            var hours = d.hours()
            var minutes = d.minutes()

            if (days < 10) days = `0${days}`
            if (hours < 10) hours = `0${hours}`
            if (minutes < 10) minutes = `0${minutes}`

            parsedData[field.name] = `${days}${hours}${minutes}--`
            break
          // Remove master data validation when the id is null
          case 'customer_id':
          case 'billed_customer_id':
            if (data[field.showKey].id === null || data[field.showKey].id === undefined) {
              if (data[field.showKey].id === null) {
                parsedData[field.name] = data[field.showKey].name

                this.assignAddressListGetter('origin_address', null)
                this.assignAddressListGetter('destination_address', null)
              }

              delete field.validation
            } else {
              this.assignAddressListGetter('origin_address', data[field.showKey].id)
              this.assignAddressListGetter('destination_address', data[field.showKey].id)
            }
            break
          case 'origin_address_id':
          case 'destination_address_id':
            if (data[field.showKey].id === null || data[field.showKey].id === undefined) {
              delete field.validation
            }
            break
          case 'customer_name':
          case 'billed_customer_name':
            parsedData[field.name] = data[field.showKey].name
            break
          default:
            break
        }
      })

      // Assign custom fields value
      data?.user_definition_fields.forEach((field) => {
        parsedData[field.key] = field.value
      })

      return parsedData
    },
    async showData(id = null) {
      this.hasCalculatedOnce = false
      this.isLoading = true

      const res = await DeliveryOrder.show(id ?? this.$route.params.id)

      if (res?.status === 403 || res?.status === 404) {
        const data = {
          hasButton: true,
        }
        localStorage.setItem(res.status, JSON.stringify(data))
        this.$router.push({
          name: res.status,
        })
        return
      }

      if (!res) return

      let resActivites = { data: [] }

      resActivites = await DeliveryActivity.get(this.$route.params.id)
      if (!resActivites) return

      this.showResponse = res.data
      this.totalWeights = res.data.load_weight
      this.totalVolumes = res.data.load_volume
      this.loadMinTemp = res.data.load_min_temperature
      this.loadMaxTemp = res.data.load_max_temperature

      const otherErrors = res.other_errors
      const errors = res?.errors

      this.deliveryNumber = this.showResponse.number
      this.shipmentType = this.showResponse?.shipment_type ?? 'UNSCHEDULED'
      const transitCount = (this.showResponse.transits || []).length
      this.fleetTaskNumber =
        this.shipmentType === 'TRANSIT'
          ? `Multi FO (${transitCount})`
          : this.showResponse.fleet_task?.number
      this.refOrder = this.showResponse.external_reference_number
      await this.$store.dispatch('delivery/setDeliveryStatus', this.showResponse.status.name)
      await this.$store.dispatch('delivery/setFleetTaskId', this.showResponse?.fleet_task?.id)
      await this.$store.dispatch('delivery/setActivities', resActivites.data)
      await this.$store.dispatch('delivery/setShipmentType', this.shipmentType)

      this.readOnlyDeliveryItems = this.readOnlyDeliveryItems.filter((item) => {
        if (item.scopes) {
          const hasAccess = this.hasAccessAll(item.scopes)
          return hasAccess
        }
        return true
      })

      this.readOnlyDeliveryItems.forEach((item, index) => {
        let value
        let bold = false
        let primaryText = false
        let isShow = true
        switch (item.key) {
          case 'deliveryType':
            value =
              this.shipmentType === 'TRANSIT'
                ? this.$t('delivery.status.transit')
                : this.shipmentType === 'DIRECT'
                ? this.$t('delivery.status.direct')
                : '-'
            bold = true
            isShow = true
            break

          case 'lineOrder':
            value = this.showResponse.line_order ? this.showResponse.line_order : '-'
            isShow = hasLineOrderFeatureAccess() ? true : false
            break

          case 'deliveryNumber':
            value = this.deliveryNumber
            bold = true
            break

          case 'refOrder':
            value = this.refOrder ? this.refOrder : this.$t('delivery.fields.fromOrder')
            break

          case 'fleetTaskNumber':
            value = this.fleetTaskNumber ? this.fleetTaskNumber : '-'
            bold = true
            primaryText = true
            isShow = true
            delete item.component
            delete item.useComponentOnly
            delete item.route
            if (this.shipmentType === 'TRANSIT') {
              item.useComponentOnly = true
              item.component = FleetTaskMultiFoDropdown
            } else if (this.showResponse?.fleet_task?.id) {
              item.route = {
                name: 'Fleet Task Detail',
                params: { id: this.showResponse.fleet_task.id },
              }
            }
            break

          case 'createdDate':
            value = formatDateWithTz(this.showResponse.created_on)
            bold = true
            break

          case 'createdBy':
            value = this.showResponse.created_by
            break

          case 'invoiceNumber':
            value = this.showResponse.invoice?.number || '-'
            break

          default:
            break
        }

        this.$set(this.readOnlyDeliveryItems, index, {
          ...item,
          value,
          bold,
          primaryText,
          isShow,
        })
      })

      if (
        this.showResponse?.delivery_notes?.length > 0 ||
        this.showResponse?.customer_notes?.length > 0 ||
        this.showResponse?.note
      ) {
        this.$refs['other-delivery-info-switch-ref'].isEnabled = true
      }

      // assign fullObject
      this.originAddressFullObject = this.showResponse.origin_address
      this.destinationAddressFullObject = this.showResponse.destination_address
      this.shippedCustomerFullObject = this.showResponse.shipped_customer
      this.marketingUserObjSelected = this.showResponse.marketing_user
      this.boxTypeFullObject = this.showResponse.box_type
      this.vendorFullObject = this.showResponse.vendor
      this.transits = this.showResponse.transits
      this.isLoadAdjusted = this.showResponse.is_load_adjusted
      this.userGroupObjSelected = this.showResponse.user_group

      await this.$nextTick()

      const parsedData = this.parseData(this.showResponse)
      const unmaskedData = maskFormValues(parsedData, [
        ...this.fields,
        ...this.transformCustomFieldResponse(this.showResponse?.user_definition_fields),
      ])
      this.formValues = unmaskedData
      this.deliveryOrderData = unmaskedData
      this.isLoading = false

      await this.$nextTick()
      this.billedCustomerFullObject = this.showResponse.billed_customer

      // assign the errors
      if (errors) {
        await this.assignErrors(errors)
      }

      if (this.$route.query.isCreatedExternally) {
        this.$mcToast.success({
          component: 'McToastDynamicBody',
          props: {
            contents: [
              { tag: 'strong', text: res.data.number, class: '__blue-highlight-text' },
              { tag: 'span', text: this.$t('delivery.message.savedAs') },
              {
                tag: 'strong',
                text: this.setStatusTMS('delivery.status', 'Dikonfirmasi'),
                class: '__status',
              },
            ],
          },
        })
        // Remove isCreatedExternally query only
        const query = Object.assign({}, this.$route.query)
        delete query.isCreatedExternally
        this.$router.replace({ query })
      }

      if (otherErrors?.delivery_order[0] === 'accurate delivery data is changed') {
        this.alertMessage = this.$t('delivery.provider.warning.deliveryOrderChanged', {
          deliveryNumber: this.deliveryNumber,
        })
        this.accurateDataIsUpdated = true
      }

      if (otherErrors?.delivery_order[0] === 'accurate delivery data is deleted') {
        this.alertMessage = this.$t('delivery.provider.warning.deliveryOrderAlreadyDeleted')
        this.accurateDataIsUpdated = true
      }

      if (this.isBoxTypeChanges) {
        this.$set(this.formErrors, 'box_type_id', this.$t('delivery.alert.invalidBoxTypeForLoad'))
      }

      this.canAddProduct = true

      this.isDataComplete = true
    },
    filterUnchangedData(newValue, oldValue) {
      const keys = new Set([...Object.keys(newValue), ...Object.keys(oldValue)])
      const _newValue = {}

      keys.forEach((key) => {
        // if both value is falsy
        if (!oldValue[key] && !newValue[key]) {
          // Zero and empty string are considered different values
          if (
            (oldValue[key] === 0 && newValue[key] === '') ||
            (oldValue[key] === '' && newValue[key] === 0)
          ) {
            _newValue[key] = newValue[key]
          }
          return
        }

        // if oldValue has same value with newValue
        if (oldValue[key] == newValue[key]) return

        // if the oldValue is 'Invalid date' and newValue is null
        if (oldValue[key] === 'Invalid date' && newValue[key] === null) return

        // if it is array
        if (Array.isArray(oldValue[key]) || Array.isArray(newValue[key])) {
          // if oldValue is undefined but the newValue is empty array
          if (!oldValue[key] && newValue[key].length === 0) return

          // matching oldValue and newValue array
          const sortedNewValue = newValue[key]?.length ? newValue[key].sort() : newValue[key]
          const sortedOldValue = oldValue[key] ? oldValue[key].sort() : oldValue[key]

          const isSameArray = isEqual(sortedNewValue, sortedOldValue)

          if (isSameArray) return
        }

        _newValue[key] = newValue[key]
      })

      const entries = Object.entries(_newValue)
      const hasChanged = entries.length > 0 && entries.every((value) => value)
      return [_newValue, hasChanged]
    },
    formatFormValues(unmaskedData) {
      const result = Object.assign(
        {},
        ...Object.keys(unmaskedData).map((key) => {
          const value = unmaskedData[key]

          if (['origin_service_time', 'destination_service_time'].includes(key)) {
            if (typeof value === 'undefined') return { [key]: '' }

            const days = Number(value.substring(0, 2))
            const hours = Number(value.substring(2, 4))
            const minutes = Number(value.substring(4, 6))
            return { [key]: `P${days}DT${hours}H${minutes}M` }
          } else if (
            ['origin_address_id', 'destination_address_id', 'user_group_id'].includes(key)
          ) {
            return { [key]: Number(value) }
          } else if (key === 'vendor_id') {
            if (value === null || value === undefined || value === '') {
              if (!this.isAdding) {
                return { [key]: value }
              }
              return {}
            }
            return { [key]: value }
          } else if (this.customFields.some((field) => field.key === key)) {
            const field = this.customFields.find((field) => field.key === key)

            if (field._type === 'number') {
              if (value == '0') return { [key]: Number(value) }
              if (field.is_required) return { [key]: value === '' ? null : Number(value) }

              return { [key]: Number(value) || null }
            }
          }

          return { [key]: value }
        })
      )
      return result
    },
    handleSwitchToggle(value) {
      this.isSwitched = value
    },
    routingBack() {
      const query = { ...this.$route.query }
      let params = {}

      let routeName = query.navsource || 'Delivery'
      routeName = routeName === 'Fleet Task Detail' ? 'Delivery' : routeName
      delete query.navsource

      if (query.navsourceparam) {
        params = JSON.parse(query.navsourceparam)
      }

      if (query.duplicated) {
        delete query.duplicated
      }

      this.$router.push({ name: routeName, params, query })
    },
    handleBack() {
      if (this.hasChanged && this.activeTab == 'INFORMATION') {
        return this.$refs['mc-modal-back'].show()
      }

      this.routingBack()
    },
    async handleCancel() {
      // NOTE: this is temporary until found better solution to solve bugs in edge cases
      this.formValues = {}
      this.marketingUserObjSelected = {}
      this.resetErrors()

      if (!this.isAdding) {
        this.shippedCustomerFullObject = this.showResponse.shipped_customer
        await this.$nextTick()

        this.marketingUserObjSelected = this.showResponse.marketing_user
        await this.$nextTick()

        this.userGroupObjSelected = this.showResponse.user_group
        await this.$nextTick()

        this.formValues = { ...this.deliveryOrderData }

        // Restore address objects from original showResponse
        this.originAddressFullObject = this.showResponse.origin_address
        this.destinationAddressFullObject = this.showResponse.destination_address

        // Use assignAddressValues to properly restore address values and readonly items
        if (this.showResponse.origin_address) {
          await this.assignAddressValues({
            key: 'origin_address',
            otherKey: 'destination_address',
            stateName: 'readOnlyOriginLocationItems',
            initial: getReadOnlyOriginLocationItems(this.$t.bind(this)),
            newValue: this.showResponse.origin_address.id,
            oldValue: null,
          })
        }

        if (this.showResponse.destination_address) {
          await this.assignAddressValues({
            key: 'destination_address',
            otherKey: 'origin_address',
            stateName: 'readOnlyDeliveryLocationItems',
            initial: getReadOnlyDeliveryLocationItems(this.$t.bind(this)),
            newValue: this.showResponse.destination_address.id,
            oldValue: null,
          })
        }

        if (this.deliveryOrderData?.vendor_id) {
          this.vendorFullObject = { ...this.showResponse.vendor }
        }
      }

      await this.assignErrors(this.errorResponse)

      this.$refs['mc-modal-cancel'].hide()
    },
    async submitStore() {
      this.track('Create Pengiriman')

      this.isSubmitting = true
      const unmaskedData = unmaskFormValues(this.formValues, this.fields)
      const formattedFormValues = this.formatFormValues(unmaskedData)

      const res = await DeliveryOrder.store(formattedFormValues)

      if (res.errors) {
        if (res.status === 400) {
          await this.assignErrors(res.errors)
        }

        this.isSubmitting = false
        return
      }

      this.$mcToast.success({
        component: 'McToastDynamicBody',
        props: {
          contents: [
            { tag: 'strong', text: res.data.number, class: '__blue-highlight-text' },
            { tag: 'span', text: this.$t('delivery.message.savedAs') },
            {
              tag: 'strong',
              text: this.setStatusTMS('delivery.status', 'Draf'),
              class: '__status',
            },
          ],
        },
      })

      const query = this.$route.query
      this.$router.push({ name: 'Delivery Detail', params: res.data, query })

      await this.$nextTick()
      await this.showData(res.data.id)
      this.isSubmitting = false
    },
    async handleAddressValueHelpApply(selectedItem) {
      if (!selectedItem || !this.isSamator) return

      const selectedAddress = Array.isArray(selectedItem) ? selectedItem[0] : selectedItem
      if (!selectedAddress) return

      this.newOriginAddress = selectedAddress
      this.$refs['mc-modal-update'].show()
    },
    async submitUpdate() {
      this.isSubmitting = true
      if(this.newOriginAddress){
        const res = await DeliveryOrder.update(
          this.$route.params.id,
          {origin_address_id: this.newOriginAddress.id}
        )

        if (res.errors) {
          this.isSubmitting = false
          this.newOriginAddress = null
          this.$refs['mc-modal-update'].hide()
          return
        }
        
        this.originAddressFullObject = this.newOriginAddress
        this.$refs[`origin_address_id-ref`][0].$children[1].fullObject = this.newOriginAddress        
        this.$set(this.addressObjSelected, 'origin_address_id', this.newOriginAddress)
        this.$set(this.formValues, 'origin_address_id', Number(this.newOriginAddress.id))

        // always setback to null
        this.newOriginAddress = null
      } else {
        let [newValue, hasChanged] = this.filterUnchangedData(this.formValues, this.deliveryOrderData)
  
        const initialBoxType = !!this.showResponse?.require_same_box_type
        const boxTypeChanged = !!this.setBoxType !== initialBoxType
  
        if (boxTypeChanged) {
          hasChanged = true
        }
  
        if (!hasChanged) {
          this.isSubmitting = false
          return
        }
  
        const unmaskedData = unmaskFormValues(newValue, this.fields)
  
        // delete the unnecessary key in unmaskedData
        let beforeFormat = {}
        Object.keys(unmaskedData).forEach((key) => {
          if (!Object.keys(newValue).includes(key)) return
          beforeFormat[key] = unmaskedData[key]
        })
  
        const formattedFormValues = this.formatFormValues(beforeFormat)
  
        const res = await DeliveryOrder.update(this.$route.params.id, formattedFormValues)
        if (res.errors) {
          if (res.status === 400) {
            await this.assignErrors(res.errors)
          }
  
          this.isSubmitting = false
          this.$refs['mc-modal-update'].hide()
          return
        }
      }  
      this.$mcToast.success(this.$t('global.alert.text.changed'))
      await this.$nextTick()
      await this.showData()

      this.isSubmitting = false
      this.$refs['mc-modal-update'].hide()
    },
    handleSubmit() {
      if (this.isSubmitting) return this.$mcToast.warning(this.$t('global.loader.pleaseWait'))

      const initialBoxType = !!this.showResponse?.require_same_box_type
      const boxTypeChanged = !!this.setBoxType !== initialBoxType

      if (boxTypeChanged) {
        this.invalidFormValues = []
      }

      if (this.$refs.formRef.hasErrors || this.invalidFieldKeys.length) {
        this.$mcToast.error(this.$t('global.inputFormatError'))
        return
      }

      if (this.isAdding) {
        this.submitStore()
      } else {
        this.$refs['mc-modal-update'].show()
      }
    },

    resetErrors() {
      this.invalidFormValues = {}
      this.formErrors = {}
      this.errorResponse = {}
    },

    async assignErrors(errors) {
      if (Object.keys(errors).length === 0) return
      const fieldErrorKeys = Object.keys(errors)

      this.resetErrors()
      this.invalidLoadFields = []

      await this.$nextTick()

      let processedErrors = 0

      const hasInvalidFleetTaskStatus = (errors) => {
        return errors?.['fields.fleet_task_uuid']?.includes('invalid fleet_task status') || false
      }

      if (hasInvalidFleetTaskStatus(errors)) {
        this.$mcToast.error(this.$t('delivery.alert.cannotCancelWaitingFleetTask'))
        return
      }

      fieldErrorKeys.forEach((fieldErrorKey) => {
        if (fieldErrorKey === 'delivery_order_loads') {
          this.invalidLoadFields = [...errors[fieldErrorKey]]
          processedErrors++
          return
        }

        if (fieldErrorKey === 'vendor_id') {
          const currentValue = this.formValues[fieldErrorKey]
          if (currentValue === undefined || currentValue === null || currentValue === '') {
            return
          }
        }

        if (fieldErrorKey === 'box_type_id' && this.isFromTransition) {
          this.forbiddenBoxType = true
        }

        this.$set(this.invalidFormValues, fieldErrorKey, this.formValues[fieldErrorKey])
        this.$set(
          this.formErrors,
          fieldErrorKey,
          apiErrorMappings[fieldErrorKey]?.[errors[fieldErrorKey][0]] ??
            this.$t('global.inputFormatError')
        )
        processedErrors++
      })

      await this.$nextTick()
      this.errorResponse = errors

      if (this.$refs['loads-table']) {
        this.$refs['loads-table'].AssignInvalidErrors()
      }

      if (processedErrors > 0) {
        this.$mcToast.error(this.$t('global.inputFormatError'))
      }
    },
    async handleTransitionStatus(data) {
      if (this.isAdding) return

      if (data.transition.status?.id !== 'CANCELED') {
        let hasErrors = this.$refs.formRef.hasErrors
        if (this.isCustomFieldShown) {
          const customFieldKeys = this.customFields.map((item) => item.key)

          const errorDefaultField = []
          for (const [key] of Object.entries(this.$refs.formRef.failingFields)) {
            if (!customFieldKeys.includes(key)) {
              errorDefaultField.push(key)
            }
          }

          if (errorDefaultField.length == 0) {
            hasErrors = false
          }
        }

        if (hasErrors || this.invalidFieldKeys.length) {
          this.$refs.formRef.showErrors()
          return this.$mcToast.error(this.$t('global.inputFormatError'))
        }
      }

      if (!data.transition.status?.id) {
        throw new Error('Transition status is not defined')
      }

      const res = await Transition.transition(this.$route.params.id, {
        status: data.transition.status.id,
        ...data.additionalPayload,
      })

      if (res.errors) {
        this.isFromTransition = true
        await this.assignErrors(res.errors)
        return
      }

      this.$mcToast.success({
        component: 'McToastDynamicBody',
        props: {
          contents: [
            { tag: 'strong', text: this.showResponse?.number, class: '__blue-highlight-text' },
            {
              tag: 'span',
              text: this.$t('delivery.message.transitionSuccess', {
                status: this.setStatusTMS('delivery.status', data.transition.status?.name)?.toLowerCase(),
              }),
            },
          ],
        },
      })

      this.isDisabled = true
      await this.showData()
    },
    async handleDuplicate() {
      if (!this.skipValidationUserGroup) {
        if (this.hasTmsUserGroupAccess) {
          this.userGroupContext.model = null
          this.$refs['mc-modal-user-group'].$refs['value-help-modal-ref'].show()
          return
        }
      }

      let data = { ids: [this.$route.params.id] }
      if (this.hasTmsUserGroupAccess) {
        data = {
          ...data,
          user_group_id: Number(this.userGroupContext.model),
        }
      }

      let res
      try {
        res = await DeliveryOrder.duplicate(data)
      } finally {
        // Only bypass the user group modal for the immediate duplicate request
        this.skipValidationUserGroup = false
      }

      if (res.errors) {
        this.$mcToast.error(this.$t('delivery.alert.duplicateFailed'))
        return
      }

      const { failed: totalFailed } = res.status

      if (totalFailed) {
        this.$mcToast.error(this.$t('delivery.alert.duplicateFailed'))
        return
      }

      window
        .open(`${window.location.origin}/delivery/${res.data.ids[0]}?duplicated=true`, '_blank')
        .focus()
    },

    async getDefaultCustomFields() {
      const res = await CustomField.get()

      if (res.errors) return

      this.defaultCustomFields = res.data
    },
    transformCustomFieldResponse(fields = []) {
      const fieldTypeMapping = { TEXT: 'text', NUMBER: 'number', DATE: 'date-picker' }
      const transformed = cloneDeep(fields)
        .map((field) => {
          // Assign null as default value
          if (this.isAdding) {
            field.value = null
          }
          // Disabled is always false when users creating a DO (every field is enabled)
          field.disabled = this.isAdding ? false : !field.is_editable

          const validation = []

          if (field.is_required) {
            validation.push('required:trim')
            field.help = true
          }

          if (field.type === 'NUMBER') {
            field._type = fieldTypeMapping.NUMBER
            field.type = fieldTypeMapping.TEXT
            validation.push('number')
          } else {
            field.type = fieldTypeMapping[field.type]
          }

          if (validation.length > 0) {
            field.validation = validation.join('|')
          }

          field.label = field.name
          field.name = field.key

          return field
        })
        .filter((field) => field.is_active)

      return transformed
    },
    refetch() {
      this.showData(this.showResponse.id)
    },
    disableSelfNote(otherDeliveryField) {
      return (
        (otherDeliveryField.name == 'customer_notes' && this.formValues?.customer_id === null) ||
        (!this.isAdding &&
          !['Draf', 'Dikonfirmasi', 'Dijadwalkan', 'Dijadwalkan Ulang', 'Ditolak Vendor'].includes(
            this.deliveryStatus
          ))
      )
    },
    getCompleteDestinationTransit() {
      if (this.activeTab !== 'INFORMATION' && this.hasCalculatedOnce) {
        return this.cachedCompleteDestinationTransit
      }

      const result = (this.transits || []).some(
        (transit) =>
          transit.destination_address &&
          transit.destination_address.id === this.destinationAddressFullObject.id
      )

      this.cachedCompleteDestinationTransit = result
      this.hasCalculatedOnce = true

      return result
    },
    handleTrackDO() {
      this.$bvModal.show('modal-link')
    },
    handleIntegrationShell() {
      this.$bvModal.show('modal-integration-shell')
    },
    handleUserGroupSelect(data) {
      this.userGroupContext.model = data
      this.skipValidationUserGroup = true

      this.$nextTick(() => {
        this.handleDuplicate()
      })
    },
  },
}
</script>

<style lang="scss" scoped>
:deep(.mc-delivery-form) {
  position: relative;

  .mc-wizard,
  .mc-header__delivery-info,
  .mc-delivery-info__card,
  .mc-delivery-info__read-only,
  .mc-origin-location__read-only,
  .mc-delivery-location__read-only {
    display: inline-block;
    margin-top: 0px;
    margin-bottom: 12px;
    width: 100%;
  }

  @include mediaQueryMinWidth('md') {
    .mc-wizard-skeleton {
      max-width: 600px;
    }
  }

  .mc-header__delivery-info,
  .mc-delivery-info__read-only,
  .mc-origin-location__read-only,
  .mc-delivery-location__read-only {
    margin-top: 12px;
  }

  .mc-delivery-info__input-group {
    display: grid;
    grid-template-columns: repeat(4, 24%);
    gap: 20px;
    margin-top: 12px;
    margin-bottom: 20px;
    justify-content: space-between;
  }

  .mc-delivery-info__input {
    width: 100%;
    padding: 0px;
  }

  .mc-origin-location__read-only,
  .mc-delivery-location__read-only {
    margin-bottom: 24px;
  }

  .card-title {
    vertical-align: center;
    margin-bottom: 0px;
  }

  .mc-location__form,
  .mc-origin-location__input-group,
  .mc-delivery-location__input-group {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
    justify-content: space-between;
  }

  .mc-location__form,
  hr {
    margin: 24px 0px;
  }

  .mc-delivery-tabs {
    margin-top: 28px;
  }

  .mc-delivery-tabs + hr {
    margin-top: 0;
  }

  .formulate-input {
    height: auto;
  }

  .formulate-input-element {
    &:has(.formulate-input__icon) {
      position: relative;
    }
  }

  .mc-box-type__title,
  .mc-vendor-name__title {
    margin-top: 24px;
    margin-bottom: 12px;
  }

  .icon-outline_info::before {
    font-size: 16px;
  }

  .vendor-fleet-status {
    margin-top: 40px;
  }

  .box-capacity,
  .vendor-fleet-status {
    // padding-bottom: 22px;

    .__label {
      color: $mep-color-text-200;
    }

    .__selected {
      color: $mep-color-text-300;
    }
  }

  .special-order__text {
    &.disabled {
      color: $mep-color-text-200;
    }
  }
}

:deep(.vendor-banner) {
  padding: 16px;
  border-radius: 4px;
  margin: 0 20px;
}

:deep(.vendor-banner--success) {
  background-color: #e7f2ee;
  border: 1px solid $green-300;
}

:deep(.vendor-banner--failed) {
  background-color: $yellow-100;
  border: 1px solid $yellow-300;
}

:deep(.multiselect) {
  .multiselect__element {
    .multiselect__option {
      text-wrap: auto;

      &--selected {
        padding-right: 30px;
      }
    }
  }
}

:deep(.formulate-input) {
  .multiselect__tags {
    max-height: fit-content;

    .multiselect__tags-wrap {
      padding: 10px 40px 10px 10px;
    }
  }
}
</style>
